import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthApis } from "../../../apis/authApis";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [success, setSuccess] = useState(false);
  
  const navigate = useNavigate();

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValidEmail = emailRegex.test(value);
    setEmailError(isValidEmail || value === "" ? "" : "Please enter a valid email address.");
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (emailError) {
      toast.error("Please enter a valid email address.");
      return;
    }
    
    setLoading(true);
    
    // Make API call to send password reset link
    AuthApis.forgotPassword({ email })
      .then((response) => {
        console?.log(response?.data)
        if (response?.data?.status === 'success') {
          setSuccess(true);
          setLoading(false);
          toast.success(response.data.message || "Password reset link sent to your email!");
        } else {
          toast.error(response.data.message || "Failed to send reset link. Please try again.");
        }
        setLoading(false);
      })
      .catch((error) => {
        console.log(error.response?.data);
        setLoading(false);
        toast.error(
          error.response?.data?.message || 
          error.response?.data?.errors?.email?.[0] || 
          "Something went wrong. Please try again."
        );
      })
      .finally(() => {
        setLoading(false);
        console.log('error');
      });
  };

  return (
    <>
      <section className="bg-[#F8F8F8] min-h-screen body-font font-poppins pb-20">
        <div className="bg-gray-100 py-10 pl-10">
          <img src="images/log.svg" alt="Logo" />
        </div>
        <div className="flex flex-col md:flex-row">
          {/* Left Section - Images */}
          <div className="bg-gray-100 flex-1 hidden md:flex flex-col items-center justify-center p-4">
            <div className="grid grid-cols-2 gap-4 w-full max-w-md">
              <img
                src="images/img4.png"
                alt="Family 1"
                className="rounded-lg object-cover w-full h-full"
              />
              <img
                src="images/img5.png"
                alt="Family 2"
                className="rounded-lg object-cover w-full h-full"
              />
             <img
                src="images/img11.png"
                alt="Family 3"
                className="rounded-lg object-cover w-full h-full"
              />
              <img
                src="images/img2.png"
                alt="Family 4"
                className="rounded-lg object-cover w-full h-full"
              />
            </div>
          </div>

          {/* Right Section - Form */}
          <div className="flex-1 flex flex-col items-center justify-center p-6">
            <div className="max-w-md w-full">
              {!success ? (
                <>
                  <h1 className="text-2xl font-bold mb-4 text-center">Reset Your Password</h1>
                  <p className="text-gray-600 mb-6 text-center">
                    Enter your email address and we'll send you a link to reset your password.
                  </p>
                  <form className="space-y-4" onSubmit={handleSubmit}>
                    <div>
                      <input
                        type="email"
                        name="email"
                        placeholder="Email Address"
                        value={email}
                        onChange={handleEmailChange}
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      {emailError && <p className="text-red-500 text-sm mt-1">{emailError}</p>}
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <p className="text-gray-600">
                        Remember your password?{' '}
                        <a href="/sign-in" className="text-blue-500 hover:underline">
                          Sign in
                        </a>
                      </p>
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-orange-500 text-white py-3 px-4 rounded-lg hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      {loading ? 'Sending...' : 'Send Reset Link'}
                    </button>
                  </form>
                </>
              ) : (
                <div className="text-center bg-white p-8 rounded-lg shadow-sm">
                  <div className="text-green-500 mb-4">
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      className="h-16 w-16 mx-auto" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" 
                      />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold mb-4">Check Your Email</h2>
                  <p className="text-gray-600 mb-6">
                    We've sent a password reset link to <span className="font-medium">{email}</span>. 
                    Please check your inbox and follow the instructions.
                  </p>
                  <p className="text-gray-500 text-sm mb-6">
                    If you don't see the email, check your spam folder.
                  </p>
                  <div className="flex flex-col space-y-3">
                    <button
                      onClick={() => setSuccess(false)}
                      className="w-full border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
                    >
                      Try Another Email
                    </button>
                    <a
                      href="/sign-in"
                      className="w-full bg-orange-500 text-white py-2 px-4 rounded-lg hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 text-center"
                    >
                      Back to Sign In
                    </a>
                  </div>
                </div>
              )}
              <div className="mt-8">
                <p className="text-center text-sm text-gray-500">
                  &copy; 2023 bETERNAL. All rights reserved.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ToastContainer
        position="bottom-left"
        autoClose={2000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  );
}

export default ForgotPassword;